import {InlineLink} from '@primer/react-brand/esm'
import {clsx} from 'clsx'

import {linkClickAnalytics} from '../../utils/analytics'
import {CloseButton} from '../CloseButton/CloseButton'
import {Countdown} from './Countdown'
import styles from './Universe26Banner.module.css'
import type {BannerCountdown, BannerDescriptor} from '../../marketing-global-banner.types'
import universe26Logo from './assets/universe_26-logo.svg'
import universe26LogoLight from './assets/universe_26-logo-light.svg'
import {Universe26BannerVisual} from './Universe26BannerVisual'

export function Universe26Banner({banner}: {banner: BannerDescriptor}) {
  const {uid, title, link, link_label, mode, artwork, countdown, live_highlight} = banner
  const isDark = mode === 'dark'
  const clickAnalytics = linkClickAnalytics(uid, title)

  return (
    <div className={clsx(styles.banner, isDark && styles.dark)}>
      <a data-analytics-event={clickAnalytics} href={link} className={styles.logolink}>
        <img src={isDark ? universe26LogoLight : universe26Logo} width={145} height={16} alt="" />
        <span className={styles.visuallyHidden}>GitHub Universe 2026</span>
      </a>

      <hr className={styles.divider} />

      <div className={styles.info}>
        {title ? (
          <span className={styles.title}>
            <Universe26Title title={title} countdown={countdown} liveHighlight={live_highlight} />
          </span>
        ) : null}

        <hr className={styles.divider} />

        <InlineLink data-analytics-event={clickAnalytics} href={link} className={styles.link}>
          {link_label}
        </InlineLink>
      </div>

      <Universe26BannerVisual artwork={artwork} />

      <CloseButton uid={uid} wrapperClassName={styles.closeWrapper} buttonClassName={styles.closeButton} />
    </div>
  )
}

function Universe26Title({
  title,
  countdown,
  liveHighlight,
}: {
  title: string
  countdown?: BannerCountdown
  liveHighlight?: string
}) {
  if (countdown) {
    return (
      <>
        {/* The ticking text is hidden from assistive technology so the alert region is not re-announced every second. */}
        <span aria-hidden="true">
          {title}
          <span className={styles.separator}>•</span>
          <Countdown to={countdown.to} />
        </span>
        <span className={styles.visuallyHidden}>{countdown.accessible_label}</span>
      </>
    )
  }

  const highlightIndex = liveHighlight ? title.lastIndexOf(liveHighlight) : -1
  if (!liveHighlight || highlightIndex === -1) return title

  return (
    <>
      <span className={styles.liveDot} aria-hidden="true" />
      {title.slice(0, highlightIndex)}
      <span className={styles.liveHighlight}>{liveHighlight}</span>
      {title.slice(highlightIndex + liveHighlight.length)}
    </>
  )
}
